import React, { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'

import styled from 'styled-components'

interface CustomOTPInputProps {
  value: string
  onChange: (value: string) => void
  length?: number
  disabled?: boolean
  error?: boolean
}

export const CustomOTPInput: React.FC<CustomOTPInputProps> = ({
  value,
  onChange,
  length = 6,
  disabled = false,
  error = false
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const [isAllSelected, setIsAllSelected] = useState(false)
  
  // Создаем массив значений для каждого поля
  const digits = value.split('').slice(0, length)
  while (digits.length < length) {
    digits.push('')
  }

  // Сброс выделения при изменении значения
  useEffect(() => {
    if (isAllSelected) {
      setIsAllSelected(false)
    }
  }, [value])

  // Обработчик клика на поле
  const handleInputClick = (index: number) => {
    if (disabled) return

    // Сбрасываем выделение всех полей
    setIsAllSelected(false)

    // Находим индекс последней заполненной ячейки
    const lastFilledIndex = digits.findIndex(digit => !digit) - 1
    
    // Если все ячейки заполнены, то последняя заполненная = length - 1
    const targetIndex = lastFilledIndex === -2 ? length - 1 : Math.max(0, lastFilledIndex + 1)
    
    // Если кликнули правее от следующей пустой ячейки, перемещаем к ней
    if (index > targetIndex) {
      inputRefs.current[targetIndex]?.focus()
    }
  }

  // Обработчик ввода
  const handleInputChange = (index: number, inputValue: string) => {
    if (disabled) return
    
    const digit = inputValue.replace(/[^0-9]/g, '').slice(-1)
    if (!digit) return

    const newValue = value.substring(0, index) + digit + value.substring(index)
    onChange(newValue.slice(0, length))
    
    if (index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  // Обработчик нажатия клавиш
  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return

    // Cmd+A / Ctrl+A - выделяем весь код
    if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      e.preventDefault()
      setIsAllSelected(true)
      return
    }

    // При любой другой клавише сбрасываем выделение
    if (isAllSelected) {
      setIsAllSelected(false)
      
      // Если это удаление, очищаем все
      if (e.key === 'Backspace' || e.key === 'Delete') {
        onChange('')
        inputRefs.current[0]?.focus()
        return
      }
      
      // Если это цифра, заменяем весь код на эту цифру
      const digit = e.key.replace(/[^0-9]/g, '')
      if (digit) {
        onChange(digit)
        inputRefs.current[1]?.focus()
        return
      }
    }

    if (e.key === 'Backspace') {
      e.preventDefault()
      let newValue
      if (index > 0) {
        newValue = value.substring(0, index - 1) + value.substring(index)
        onChange(newValue)
        inputRefs.current[index - 1]?.focus()
      } else {
        newValue = value.substring(1)
        onChange(newValue)
      }
    }

    if (e.key === 'Delete') {
      e.preventDefault()
      const newValue = value.substring(0, index) + value.substring(index + 1)
      onChange(newValue)
    }
    
    // Стрелки для навигации
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputRefs.current[index - 1]?.focus()
    }
    
    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault()
      inputRefs.current[index + 1]?.focus()
    }
  }

  // Обработчик вставки из буфера
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return
    
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '')
    onChange(pastedData.slice(0, length))
    
    const targetIndex = Math.min(pastedData.length, length - 1)
    setTimeout(() => {
      inputRefs.current[targetIndex]?.focus()
    }, 0)
  }

  // Автофокус на первое поле при монтировании
  useEffect(() => {
    if (!disabled) {
      inputRefs.current[0]?.focus()
    }
  }, [disabled])

  return (
    <SOTPContainer>
      {digits.map((digit, index) => (
        <SOTPInput
          key={index}
          ref={(el) => { inputRefs.current[index] = el }}
          type="tel"
          inputMode="decimal"
          value={digit}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          $error={error}
          $filled={!!digit}
          $selected={isAllSelected}
          // maxLength={1}
          autoComplete="one-time-code"
          onClick={() => handleInputClick(index)}
        />
      ))}
    </SOTPContainer>
  )
}

const SOTPContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
`

const SOTPInput = styled.input<{ $error: boolean; $filled: boolean; $selected: boolean }>`
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  border: 2px solid ${props => 
    props.$selected
      ? 'var(--color-primary, #8747DF)'
      : props.$error 
        ? 'var(--color-error, #ff4d4f)' 
        : props.$filled 
          ? 'var(--color-primary, #8747DF)' 
          : 'var(--color-border, #d9d9d9)'
};
  border-radius: 8px;
  background: ${props => 
    props.$selected 
      ? 'var(--color-primary, #8747DF)' 
      : props.disabled 
        ? '#f5f5f5' 
        : '#ffffff'
};
  color: ${props => 
    props.$selected 
      ? '#ffffff' 
      : props.disabled 
        ? '#00000040' 
        : '#000000'
};
  outline: none;
  transition: all 0.2s ease;
  caret-color: ${props => props.$selected ? '#ffffff' : 'var(--color-primary, #8747DF)'};

  &:focus {
    border-color: var(--color-primary, #8747DF);
    box-shadow: ${props => 
    props.$selected 
      ? '0 0 0 2px rgba(135, 71, 223, 0.4)'
      : '0 0 0 2px rgba(135, 71, 223, 0.2)'
};
  }

  &:hover:not(:disabled) {
    border-color: ${props => 
    props.$selected
      ? 'var(--color-primary, #8747DF)'
      : props.$error 
        ? 'var(--color-error, #ff4d4f)' 
        : 'var(--color-primary, #8747DF)'
};
  }

  &::selection {
    background: ${props => 
    props.$selected 
      ? 'rgba(255, 255, 255, 0.3)' 
      : 'rgba(135, 71, 223, 0.3)'
};
  }

  /* Убираем спиннеры для числовых полей */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }
` 